/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react';
import { IDropdown, IDropdownOption, IDropdownProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';

export type FileView = 'all' | 'single';

type FilesViewDropdownProps = {
    onFileViewChange: (fileView: FileView) => void;
    selectedViewingOption: FileView;
    isDisabled?: boolean;
};

const messages = defineMessages({
    allFilesView: {
        id: 'customText.fileViewDropdown.allFilesView',
        defaultMessage: 'All documents view'
    },
    singleFileView: {
        id: 'customText.fileViewDropdown.singleFileView',
        defaultMessage: 'Single document view'
    },
    filesViewDropDownAriaLabel: {
        id: 'customText.fileViewDropdown.filesViewDropDownAriaLabel',
        defaultMessage: 'documents view dropDown'
    }
});

const dropdownStyles = classNamesFunction<IDropdownProps, IDropdownStyles>()({
    title: { borderStyle: 'none', minWidth: '135px', marginTop: '3px', overflow: 'hidden' }
});

export const FilesViewDropdown = (props: FilesViewDropdownProps) => {
    const { isDisabled = false, onFileViewChange, selectedViewingOption } = props;
    const { localize } = useLocalization();
    const dropdownRef = React.useRef<IDropdown>();

    const onRenderTitle = (options: IDropdownOption[]): JSX.Element => {
        const option = options.find(o => o.key === selectedViewingOption);

        return onRenderOption(option);
    };

    React.useEffect(() => {
        if (dropdownRef.current) {
            // The timeout is a workaround to let the focus capture the dropdown after all the react updates is done.
            setTimeout(() => dropdownRef.current?.focus(), 0);
        }
    }, [dropdownRef.current, selectedViewingOption]);

    const onRenderOption = (option: IDropdownOption): JSX.Element => (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
            {option.data && option.data.icon && <Icon iconName={option.data.icon} aria-hidden="true" title={option.data.icon} />}
            <LuisText>{option.text}</LuisText>
        </Stack>
    );

    return (
        <LuisDropdown
            disabled={isDisabled}
            isSearchEnabled={false}
            styles={dropdownStyles}
            componentRef={dropdownRef}
            selectedKey={selectedViewingOption}
            onRenderOption={onRenderOption}
            onRenderTitle={onRenderTitle}
            ariaLabel={localize(messages.filesViewDropDownAriaLabel)}
            onChange={(_, option) => onFileViewChange(option.key as FileView)}
            options={[
                {
                    key: 'all',
                    text: localize(messages.allFilesView),
                    data: { icon: 'Boards' },
                    selected: selectedViewingOption === 'all'
                },
                {
                    text: localize(messages.singleFileView),
                    key: 'single',
                    data: { icon: 'Page' },
                    selected: selectedViewingOption === 'single'
                }
            ]}
        />
    );
};
